import { TermsActions, TermsActionTypes } from '../actions/terms-cond.action';
import { MainActions, MainActionTypes } from '../actions/main-actions/main.action';
import { GuardActions, GuardActionTypes } from '../actions/guard-actions/guard.action';
import { GhostLoginActions, GhostLoginActionTypes } from '../actions/ghost-login-actions/ghost-login.actions';
import { AccSettingsActions, AccSettingsActionTypes } from '../actions/acc-settings-actions/acc-settings.action';
import { BusinessActions, BusinessActionTypes } from '@app/main/store/actions/business-actions/business.actions';

import { CountriesAndCategories, Establishment, TermsAndConditions, CurrentAdmins, AdminRole } from '@app/api/models/api-models';
import { BootstrapError } from '@app/api/models/bootstrap-errors';

import { IFaqs } from './../../../api/models/faq.model';

export interface TermsState {
  termsConditions: TermsAndConditions;
  establishmentDefaultId: string;
  establishmentDefault: Establishment;
  establishments: Establishment[];
  countriesAndCategories: CountriesAndCategories;
  currentAdmins: CurrentAdmins[];
  inviteRole: AdminRole;
  inviteAdminSuccess: boolean;
  loading: boolean;
  customerUpdate: {
    updatingCustomer: boolean;
    updateState: boolean;
  };
  hasBootstrapError: BootstrapError;
  isDishAccepted: boolean;
  isSettingsPage: boolean;
  hasMigrationError: boolean;
  migrationErrorKeys: string[];
  openClaimingDialog: boolean;
  isYextAccReady: boolean;
  showSpecialGMBInfo: boolean;
  isBusinessFromHydra: boolean;
  dishUserWithNoEstablishmentScan: boolean;
  faqs: IFaqs;
  resetGMBfailed: boolean;
  activateGMBFailed: boolean;
  ghostLoginGMBActionsLoading: boolean;
  didMacUserLookupFailed: boolean;
  updateBusinessDataSuccess: boolean;
  businessUnitActions: any[];
  userActions: any[];
  upgradeActions: any[];
}

const initialState: TermsState = {
  termsConditions: null,
  establishmentDefaultId: null,
  establishmentDefault: null,
  establishments: null,
  countriesAndCategories: null,
  currentAdmins: null,
  inviteAdminSuccess: null,
  inviteRole: null,
  loading: false,
  customerUpdate: {
    updatingCustomer: false,
    updateState: null,
  },
  hasBootstrapError: null,
  isDishAccepted: false,
  isSettingsPage: false,
  hasMigrationError: false,
  migrationErrorKeys: null,
  openClaimingDialog: false,
  isYextAccReady: null,
  showSpecialGMBInfo: false,
  isBusinessFromHydra: false,
  dishUserWithNoEstablishmentScan: false,
  faqs: null,
  resetGMBfailed: false,
  activateGMBFailed: false,
  ghostLoginGMBActionsLoading: false,
  didMacUserLookupFailed: false,
  updateBusinessDataSuccess: null,
  businessUnitActions: null,
  userActions: null,
  upgradeActions: null,
};

export function TermsReducer(
  state: TermsState = initialState,
  action: TermsActions | MainActions | GuardActions | AccSettingsActions | BusinessActions | GhostLoginActions
): TermsState {
  switch (action.type) {
    case TermsActionTypes.TERMS_CONDITIONS_UPDATE_ATTEMPT:
    case TermsActionTypes.TERMS_CONDITIONS_ATTEMPT: {
      return { ...state, loading: true, hasBootstrapError: null };
    }

    case TermsActionTypes.TERMS_CONDITIONS_FAILURE: {
      return { ...state, loading: false, hasBootstrapError: BootstrapError.MISSING_ESTABLISHMENT };
    }

    case TermsActionTypes.ESTABLISHMENT_IS_SUSPENDED: {
      return { ...state, loading: false, hasBootstrapError: BootstrapError.ESTABLISHMENT_IS_SUSPENDED };
    }

    case TermsActionTypes.TERMS_CONDITIONS_SUCCESS: {
      const businessUnitActions = action.payload.response.businessUnitActions;
      const { establishment, businessUnit } = businessUnitActions.find(bu => bu.payload.establishment || bu.payload.businessUnit).payload;
      const selectedEstablisment = establishment || businessUnit;
      return {
        ...state,
        loading: false,
        establishments: [selectedEstablisment],
        establishmentDefaultId: selectedEstablisment.establishmentId,
        establishmentDefault: selectedEstablisment,
        isYextAccReady: false,
      };
    }

    // case TermsActionTypes.TERMS_CONDITIONS_UPDATE_SUCCESS: {
    //   return {
    //     // ...state, loading: false,
    //     //  termsConditions: action.payload.update
    //   }
    // }
    case TermsActionTypes.TERMS_CONDITIONS_UPDATE_FAILURE: {
      return { ...state, loading: false };
    }

    case TermsActionTypes.PURCHASE_NOT_FOUND: {
      return { ...state, loading: false, hasBootstrapError: BootstrapError.MISSING_PURCHASE };
    }
    case MainActionTypes.GET_COUNTRIES_CATEGORIES_LIST_SUCCESS: {
      return { ...state, countriesAndCategories: action.payload.list };
    }

    case MainActionTypes.GET_ADMINS_SUCCESS: {
      return { ...state, currentAdmins: action.payload.response };
    }

    case MainActionTypes.REQUEST_GMB_ADMIN_ROLE_ATTEMPT: {
      return { ...state, inviteRole: action.payload.adminRole };
    }

    case MainActionTypes.REQUEST_GMB_ADMIN_ROLE_SUCCESS: {
      return { ...state, inviteAdminSuccess: true };
    }

    case MainActionTypes.REQUEST_GMB_ADMIN_ROLE_FAILURE: {
      return { ...state, inviteAdminSuccess: false };
    }

    case MainActionTypes.RESET_INVITE_STATUS: {
      return { ...state, inviteAdminSuccess: null };
    }

    case MainActionTypes.GET_FAQS_SUCCESS: {
      return { ...state, faqs: action.payload };
    }

    case AccSettingsActionTypes.CUSTOMER_UPDATE_ATTEMPT: {
      return { ...state, customerUpdate: { updatingCustomer: true, updateState: null } };
    }

    case AccSettingsActionTypes.CUSTOMER_UPDATE_SUCCESS: {
      return { ...state, customerUpdate: { updatingCustomer: false, updateState: true } };
    }

    case AccSettingsActionTypes.CUSTOMER_UPDATE_FAILURE: {
      return { ...state, customerUpdate: { updatingCustomer: false, updateState: false } };
    }

    case GuardActionTypes.OPEN_SETTINGS_PAGE: {
      return { ...state, isSettingsPage: true };
    }

    case GuardActionTypes.CLOSE_SETTINGS_PAGE: {
      return { ...state, isSettingsPage: false };
    }

    case GuardActionTypes.BYPASS_ESTABLISHMENT_VERIFICATION: {
      return { ...state, loading: false, dishUserWithNoEstablishmentScan: true };
    }

    case GuardActionTypes.SET_ESTABLISMENT_ID: {
      if (state.establishments && state.establishments.length > 0) {
        const selectedEstablisment = state.establishments.find(x => x.establishmentId === action.payload.establishmentId);
        return {
          ...state,
          establishmentDefaultId: selectedEstablisment ? selectedEstablisment.establishmentId : state.establishments[0].establishmentId,
          establishmentDefault: selectedEstablisment ? selectedEstablisment : state.establishments[0],
        };
      }
      return { ...state, establishmentDefaultId: action.payload.establishmentId };
    }

    case TermsActionTypes.FAILED_AUTHENTICATION: {
      return { ...state, loading: false, hasBootstrapError: BootstrapError.FAILED_AUTHENTICATION };
    }

    case TermsActionTypes.GET_ACTIONS_SUCCESS: {
      const { businessUnitActions, userActions, upgradeActions } = action.payload;
      return { ...state, businessUnitActions, userActions, upgradeActions };
    }

    case GhostLoginActionTypes.ACTIVATE_GMB_CHANNEL_ATTEMPT: {
      return { ...state, activateGMBFailed: false, ghostLoginGMBActionsLoading: true, resetGMBfailed: false };
    }

    case GhostLoginActionTypes.ACTIVATE_GMB_CHANNEL_SUCCESS: {
      return { ...state, ghostLoginGMBActionsLoading: false };
    }

    case GhostLoginActionTypes.ACTIVATE_GMB_CHANNEL_FAILURE: {
      return { ...state, activateGMBFailed: true, ghostLoginGMBActionsLoading: false };
    }

    case GhostLoginActionTypes.RESET_GMB_CHANNEL_ATTEMPT: {
      return { ...state, resetGMBfailed: false, ghostLoginGMBActionsLoading: true, activateGMBFailed: false };
    }

    case GhostLoginActionTypes.RESET_GMB_CHANNEL_SUCCESS: {
      return { ...state, ghostLoginGMBActionsLoading: false };
    }

    case GhostLoginActionTypes.RESET_GMB_CHANNEL_FAILURE: {
      return { ...state, resetGMBfailed: true, ghostLoginGMBActionsLoading: false };
    }

    case BusinessActionTypes.UPDATE_BUSINESS__FAILURE: {
      return { ...state, updateBusinessDataSuccess: false };
    }

    case BusinessActionTypes.UPDATE_BUSINESS_SUCCESS: {
      return { ...state, updateBusinessDataSuccess: true };
    }

    case BusinessActionTypes.UPDATE_BUSINESS_ATTEMPT: {
      return { ...state, updateBusinessDataSuccess: null };
    }

    case BusinessActionTypes.GET_ACTIONS: {
      const { businessUnitActions, userActions, upgradeActions } = action.payload;
      return { ...state, businessUnitActions, userActions, upgradeActions, loading: false };
    }

    default:
      return state;
  }
}
